import { createAsyncThunk, createEntityAdapter, createSlice } from "@reduxjs/toolkit";

import { Lecture } from "../../typings/Lecture";
import { SliceStatus } from "../utils";
import lecturesApi from "./lecturesApi";

export const lecturesAdapter = createEntityAdapter<Lecture>();

const initialState = lecturesAdapter.getInitialState<{
  status: SliceStatus;
}>({
  status: SliceStatus.IDLE,
});

export const getLectures = createAsyncThunk(
  "lectures/getLectures",
  async (params: {
    page: number;
    topicIds: number[];
    areaIds: number[];
    instructorIds: number[];
    date: Nullable<string>;
  }) => {
    const data = await lecturesApi.getLectures(params);
    return data; 
  }
);

export const createLecture = createAsyncThunk(
  "lectures/createLecture",
  async (lectureData: Partial<Lecture>, { rejectWithValue }) => {
    try {
      return await lecturesApi.createLecture(lectureData);
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to create lecture");
    }
  }
);

export const deleteLecture = createAsyncThunk(
  "lectures/deleteLecture",
  async (lectureId: number, { rejectWithValue }) => {
    try {
      await lecturesApi.deleteLecture(lectureId);
      return lectureId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data || "Failed to delete lecture");
    }
  }
);

export const lecturesSlice = createSlice({
  name: "lectures",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getLectures.pending, (state) => {
      state.status = SliceStatus.LOADING;
    });

    builder.addCase(getLectures.fulfilled, (state, action) => {
      lecturesAdapter.setAll(state, action.payload);
      state.status = SliceStatus.IDLE;
    });

    builder.addCase(getLectures.rejected, (state) => {
      state.status = SliceStatus.FAILED;
    });

    builder.addCase(createLecture.fulfilled, (state, action) => {
      lecturesAdapter.addOne(state, action.payload);
    });

    builder.addCase(deleteLecture.fulfilled, (state, action) => {
      lecturesAdapter.removeOne(state, action.payload);
    });
  },
});

export default lecturesSlice.reducer;
